<template>
    <div class="relative">
        <div class="sticky top-0 z-50 bg-white w-full min-h-[62px]">
            <placement-header-alt
                :placement="mainPlacement"
                :placementData="currentGroupActual"
                :index="0"
                :has-error="inErrorState"
            >
                <div v-if="inErrorState"
                    class="text-red-error text-sm"
                >
                    {{ $t('SELECT_STYLE_NO_STAMPING') }}
                </div>
            </placement-header-alt>
        </div>

        <div class="mt-[29.73px]"></div>

        <div class="px-[20px] lg:px-[40px]">
            <step-options-initials-buttons
                v-for="(option, index) in styleOptions"
                :key="option.code"
                :index="index"
                :class="{
                    'mt-[24px]': index > 0,
                }"
                :main-placement="mainPlacement"
                :option="option"
                @unselect="handleSelectNothing"
                @select="selectActual"
                :scroll-to-offset="96"
                :is-dirty="isDirty"
                :has-error-state="inErrorState"
                background-size="auto 100%"
            >
            </step-options-initials-buttons>
        </div>
    </div>
</template>

<script>
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';
    import StepOptionsInitialsButtons from './buttons/StepOptionsInitialsButtons.vue';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            PlacementHeaderAlt,
            StepOptionsInitialsButtons,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
            isDirty: {
                type: Boolean,
                default: false,
            },
        },

        emits: ['applySelections'],

        computed: {
            inErrorState() {
                if (!this.isDirty) {
                    return false;
                }

                if (
                    this.customizerStore.panelsInErrorState.some((x) => x.startsWith('Character Stamping'))
                    || this.selectedValue === this.nonOptionalPlaceholder
                    || !this.isComplete
                ) {
                    return true;
                }

                return false;
            },

            visibleGroups() {
                return this.customizerStore.visibleGroups;
            },

            currentGroupActual() {
                return this.visibleGroups.find((group) => group.name === this.currentGroupName);
            },

            currentGroupName() {
                return this.customizerStore.currentGroup;
            },

            selectionsMade() {
                return this.customizerStore.selectionsMade;
            },

            isDisabled() {
                return this.selectedValue === this.nonOptionalPlaceholder.code || this.inErrorState;
            },

            optionalPlaceholder() {
                return this.mainPlacement.components.find(
                    (x) => x.placeholder
                        && this.viewController.isAvailable(x)
                        && x.placeholderOptional,
                )?.code;
            },

            nonOptionalPlaceholder() {
                return this.mainPlacement.components.find(
                    (x) => x.placeholder
                        && this.viewController.isAvailable(x)
                        && !x.placeholderOptional,
                )?.code;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            panelToReset() {
                return this.customizerStore.panelToReset;
            },

            visiblePlacements() {
                const placements = this.customizerStore.visiblePlacements(this.itemData) ?? [];

                return placements.map((placement) => {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    // Add extra properties to each placement:
                    // - a code of the currently selected component.
                    return {
                        selectedValue: selected ? selected.code : null,
                        groupType: placement.custom?.['v-group-type'] ?? 'standard-buttons',
                        ...placement,
                    };
                });
            },

            mainPlacement() {
                return this.visiblePlacements.find((x) => x.code === 'StampingStyle');
            },

            selectedValue() {
                return this.mainPlacement.selectedValue;
            },

            styleOptions() {
                return this.customizerStore.sortOptions(this.mainPlacement, this.selectedValue);
            },

            isComplete() {
                return this.selectionsMade?.includes('Character Stamping');
            },
        },

        methods: {
            handleApplySelections(hasError) {
                if (hasError) {
                    return;
                }

                this.appStore.setShowGroupOverlay(false);

                this.$emit('applySelections');
            },

            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            selectValue(placement, option) {
                this.customizerStore.setSelection(placement.code, option.value, false);
            },

            async handleSelectNothing() {
                this.viewController.updateComponent(this.mainPlacement.code, this.optionalPlaceholder.code);

                await this.$nextTick();
                if (this.isComplete) {
                    this.customizerStore.removePanelFromErrorState('Character Stamping');
                }
            },

            async selectActual({ placement, option }) {
                this.customizerStore.selectStampingStyle(placement, option);

                await this.$nextTick();
                if (this.isComplete) {
                    this.customizerStore.removePanelFromErrorState('Character Stamping');
                }
            },
        },
    };
</script>
